// --- React & Redux
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
// --- External UI Libraries
import { Col, Row } from 'antd';
import Button from '@dtci/spellbook/Button';
import Input from '@dtci/spellbook/Input';
// --- Components
import { InputLabel } from 'datastage/components/InputLabel';
// --- Actions & Hooks
import { useProductGateActions } from 'datastage/store/productGate/useProductGate';
import { useProductGateData } from '../../hooks/useProductGateData';
// --- Utilities
import { getItemRange } from 'datastage/utils/pagination';
import { parseSearchParams, updateSearchParams } from 'datastage/utils/urlParams';
import { cleanProductGateFilter } from './utils/productGateSearchUtils';
import { validateSpecialCharacters } from 'datastage/utils/validateSpecialCharacters';
// --- Styles
import './styles.scss';

/**
 * Rightsline Product Gate Search Form
 *
 * Features:
 * - Renders a filter form for Rightsline Product Gate (From Date, To Date, Title, Group ID, Product ID)
 * - Debounced auto-search on input change (400ms delay)
 * - Input validation for Group ID and Product ID fields
 * - Syncs filter state with URL params bidirectionally
 * - Reset button clears all filters
 *
 * @returns {JSX.Element}
 */
export function RightslineProductGateSearchForm() {
    const navigate = useNavigate();
    const location = useLocation();
    const isInitialMount = useRef(true);
    const debounceTimerRef = useRef(null);

    const {
        fetchProductGates,
        updateProductGate,
        productGateRange,
        productGateSort,
        espnTitle,
        itemsPerPage,
    } = useProductGateData();
    const { updateProductGateFrom, clearProductGate } = useProductGateActions();
    const { filterForm, dataToggle } = useSelector((s) => s.productGate);

    // Validation state for inline error messages
    const [validationErrors, setValidationErrors] = useState({
        groupId: undefined,
        productId: undefined,
    });

    // Sync URL params to form state on navigation (location.key changes on each navigation)
    useEffect(() => {
        const params = parseSearchParams(location.search);
        const filterData = buildFilterFromParams(params);

        // Handle pageSize from URL (TAP pattern)
        let rangeOverride;
        if (params.pageSize) {
            const newPageSize = parseInt(params.pageSize, 10);
            if (!isNaN(newPageSize) && newPageSize > 0) {
                rangeOverride = getItemRange(0, newPageSize);
                updateProductGate({
                    itemsPerPage: newPageSize,
                    productGateRange: rangeOverride,
                    currentPage: 0,
                });
            }
        }

        const hasUrlParams = Object.keys(filterData).length > 0;

        if (hasUrlParams) {
            updateProductGateFrom({ key: 'filterForm', data: filterData });
        }

        // Merge URL-derived filters onto Redux filterForm so defaults apply when the URL
        // only carries partial params (e.g. pageSize).
        const filterForFetch = hasUrlParams ? { ...filterForm, ...filterData } : filterForm;

        fetchData(filterForFetch, rangeOverride);
        isInitialMount.current = false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.key, dataToggle]);

    /**
     * Builds filter form data from URL search params.
     * Converts string params to appropriate types.
     */
    function buildFilterFromParams(params) {
        const filterData = {};

        if (params.fromDate) filterData.fromDate = params.fromDate;
        if (params.toDate) filterData.toDate = params.toDate;
        if (params.title) filterData.title = params.title;
        if (params.groupId) filterData.groupId = params.groupId;
        if (params.productId) filterData.productId = params.productId;

        // Convert boolean fields
        const booleanKeys = ['hideNA', 'hideIgnored', 'hideCompleted', 'hideUpdates'];
        booleanKeys.forEach((key) => {
            if (key in params) {
                filterData[key] = params[key] === 'true' || params[key] === true;
            }
        });

        return filterData;
    }

    /**
     * Fetches product gate data and updates Redux state.
     * @param {Object} filterData - Filter criteria
     * @param {Array} rangeOverride - Optional range override (e.g., when pageSize changes)
     */
    const fetchData = async (filterData, rangeOverride) => {
        // Merge with current filterForm to include toggle states
        const mergedFilter = { ...filterForm, ...filterData };
        const range = rangeOverride ?? getItemRange(0, itemsPerPage);
        const data = await fetchProductGates(range, mergedFilter);

        // Check for error (has code field) or null (network/auth error)
        if (!data || data.code) {
            // Error already shown by fetchProductGates
            updateProductGate({
                productUpdates: [],
                totalPages: 0,
                currentPage: 0,
                totalItems: 0,
            });
            return;
        }

        updateProductGate({
            productUpdates: data?.content ?? [],
            totalPages: data?.totalPages ?? 0,
            currentPage: data?.pageable?.pageNumber ?? 0,
            totalItems: data?.totalElements ?? 0,
            productGateRange: range,
        });
    };

    /**
     * Reset navigates to clean URL; the location.key useEffect handles re-fetch.
     */
    const handleResetFilter = () => {
        clearProductGate();
        setValidationErrors({ groupId: undefined, productId: undefined });
        navigate(location.pathname);
    };

    /**
     * Handles input changes with validation and debounced search.
     * Validates Group ID and Product ID fields for special characters.
     * Triggers search after 400ms of no typing.
     */
    function handleInputChange(field, value) {
        let newValidationErrors = validationErrors;

        // Validate ID fields for special characters
        if (field === 'groupId' || field === 'productId') {
            const error = validateSpecialCharacters(value);
            newValidationErrors = { ...validationErrors, [field]: error };
            setValidationErrors(newValidationErrors);
        }

        // Update form state
        const newFilterForm = { ...filterForm, [field]: value };
        updateProductGateFrom({
            key: 'filterForm',
            data: { [field]: value },
        });

        // Debounce the search to avoid excessive API calls
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(() => {
            const hasErrors = Object.values(newValidationErrors).some((error) => error);
            if (!hasErrors) {
                const firstPageRange = getItemRange(0, itemsPerPage);
                updateProductGate({ productGateRange: firstPageRange, currentPage: 0 });
                const filtered = cleanProductGateFilter(
                    newFilterForm,
                    firstPageRange,
                    productGateSort,
                );
                updateSearchParams(location, navigate, { ...filtered, espnTitle });
            }
        }, 400);
    }

    return (
        <Row className="product-updates-filter-form">
            <Col>
                <InputLabel colProps={{ span: 24 }} label="From">
                    <Input
                        labelText="Date Input"
                        type="date"
                        value={filterForm.fromDate}
                        onChange={(e) => handleInputChange('fromDate', e.target.value)}
                    />
                </InputLabel>
            </Col>
            <Col>
                <InputLabel colProps={{ span: 24 }} label="To">
                    <Input
                        labelText="Date Input"
                        type="date"
                        value={filterForm.toDate}
                        onChange={(e) => handleInputChange('toDate', e.target.value)}
                    />
                </InputLabel>
            </Col>
            <Col>
                <InputLabel colProps={{ span: 24 }} label="Title">
                    <Input
                        value={filterForm.title}
                        onChange={(e) => handleInputChange('title', e.target.value)}
                        width="auto"
                        id="title"
                        name="title"
                        placeholder="Enter"
                    />
                </InputLabel>
            </Col>
            <Col>
                <InputLabel colProps={{ span: 24 }} label="Group ID">
                    <Input
                        value={filterForm.groupId}
                        onChange={(e) => handleInputChange('groupId', e.target.value)}
                        width="auto"
                        id="groupId"
                        name="groupId"
                        placeholder="Enter"
                        className={validationErrors.groupId ? 'input-error' : ''}
                    />
                    {validationErrors.groupId && (
                        <div className="validation-error">{validationErrors.groupId}</div>
                    )}
                </InputLabel>
            </Col>
            <Col>
                <InputLabel colProps={{ span: 24 }} label="Product ID">
                    <Input
                        value={filterForm.productId}
                        onChange={(e) => handleInputChange('productId', e.target.value)}
                        width="auto"
                        id="productId"
                        name="productId"
                        placeholder="Enter"
                        className={validationErrors.productId ? 'input-error' : ''}
                    />
                    {validationErrors.productId && (
                        <div className="validation-error">{validationErrors.productId}</div>
                    )}
                </InputLabel>
            </Col>
            <Col>
                <Button
                    className="reset-button"
                    sizeType="small"
                    styleType="standard"
                    onClick={handleResetFilter}
                >
                    Reset
                </Button>
            </Col>
        </Row>
    );
}
